import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import type { z } from 'zod';
import {
  propertyBasicsSchema,
  propertyOwnershipSchema,
  buildingTypes,
  type BuildingType,
  type PropertyType,
} from '@vuokramani/shared';
import { PageHeader, pageStyles as s } from '../../components/page.tsx';
import { Alert, Button, ChoiceGroup, Panel, SelectField, TextField } from '../../components/ui.tsx';
import { errorKey } from '../../lib/api.ts';
import { toNumber, useCreateProperty } from '../../lib/properties.ts';

type Errors = Record<string, string>;

interface FormState {
  type: PropertyType;
  buildingType: BuildingType | '';
  streetAddress: string;
  postalCode: string;
  city: string;
  name: string;
  housingCompany: string;
  housingCompanyBusinessId: string;
  areaM2: string;
  roomLayout: string;
  ownershipPercent: string;
  tenancy: 'later' | 'now';
}

const INITIAL: FormState = {
  type: 'apartment',
  buildingType: '',
  streetAddress: '',
  postalCode: '',
  city: '',
  name: '',
  housingCompany: '',
  housingCompanyBusinessId: '',
  areaM2: '',
  roomLayout: '',
  ownershipPercent: '100',
  tenancy: 'later',
};

function collectErrors(error: z.ZodError): Errors {
  const errors: Errors = {};
  for (const issue of error.issues) {
    const key = String(issue.path[0]);
    if (!errors[key])
      errors[key] = issue.message.startsWith('errors.') ? issue.message : 'errors.required';
  }
  return errors;
}

const STEPS = ['basics', 'ownership', 'tenancy'] as const;

/** Asunnon lisäys kolmessa vaiheessa: perustiedot, omistus, vuokrasuhde. */
export function NewPropertyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateProperty();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<Errors>({});

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setForm((f) => ({ ...f, [key]: value }));
  const err = (key: string) => (errors[key] ? t(errors[key]) : undefined);

  const basics = () =>
    propertyBasicsSchema.safeParse({
      type: form.type,
      buildingType: form.buildingType || null,
      streetAddress: form.streetAddress,
      postalCode: form.postalCode,
      city: form.city,
      name: form.name,
      housingCompany: form.housingCompany,
      housingCompanyBusinessId: form.housingCompanyBusinessId,
      areaM2: toNumber(form.areaM2),
      roomLayout: form.roomLayout,
    });
  const ownership = () =>
    propertyOwnershipSchema.safeParse({ ownershipPercent: toNumber(form.ownershipPercent) });

  function next(event: FormEvent) {
    event.preventDefault();
    const result = step === 0 ? basics() : ownership();
    if (!result.success) {
      setErrors(collectErrors(result.error));
      return;
    }
    setErrors({});
    setStep((s) => s + 1);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const b = basics();
    const o = ownership();
    if (!b.success || !o.success) return;
    create.mutate(
      { ...b.data, ...o.data },
      {
        onSuccess: (p) =>
          void navigate(
            form.tenancy === 'now'
              ? `/vuokranantaja/asunnot/${p.id}/vuokrasuhde/uusi`
              : `/vuokranantaja/asunnot/${p.id}`,
            { replace: true },
          ),
      },
    );
  }

  return (
    <>
      <PageHeader
        title={t('newProperty.title')}
        breadcrumb={
          <>
            <Link to="/vuokranantaja/asunnot">{t('property.breadcrumb')}</Link>
            <ChevronRight size={14} aria-hidden="true" />
            <span>{t('newProperty.title')}</span>
          </>
        }
      />

      <div className={s.wizard}>
        <ol
          className={s.steps}
          aria-label={t('newProperty.stepOf', { current: step + 1, total: 3 })}
        >
          {STEPS.map((key, index) => (
            <li
              key={key}
              className={`${s.step} ${index === step ? s.stepCurrent : ''} ${index < step ? s.stepDone : ''}`}
              aria-current={index === step ? 'step' : undefined}
            >
              <span className={s.stepNumber}>{index + 1}</span>
              <span>{t(`newProperty.steps.${key}`)}</span>
            </li>
          ))}
        </ol>

        <Panel>
          {step === 0 && (
            <form className={s.form} onSubmit={next} noValidate>
              <ChoiceGroup<PropertyType>
                name="type"
                legend={t('newProperty.type')}
                value={form.type}
                onChange={set('type')}
                options={[
                  { value: 'apartment', title: t('propertyTypes.apartment') },
                  {
                    value: 'house',
                    title: t('propertyTypes.house'),
                    text: t('newProperty.typeLater'),
                    disabled: true,
                  },
                ]}
              />
              <SelectField
                label={t('property.fields.buildingType')}
                value={form.buildingType}
                onChange={(e) => set('buildingType')(e.target.value as BuildingType | '')}
              >
                <option value="">{t('common.notSet')}</option>
                {buildingTypes.map((b) => (
                  <option key={b} value={b}>
                    {t(`buildingTypes.${b}`)}
                  </option>
                ))}
              </SelectField>
              <TextField
                label={t('newProperty.streetAddress')}
                hint={t('newProperty.streetAddressHint')}
                autoComplete="street-address"
                value={form.streetAddress}
                onChange={(e) => set('streetAddress')(e.target.value)}
                error={err('streetAddress')}
              />
              <div className={s.formRowNarrow}>
                <TextField
                  label={t('newProperty.postalCode')}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  maxLength={5}
                  value={form.postalCode}
                  onChange={(e) => set('postalCode')(e.target.value)}
                  error={err('postalCode')}
                />
                <TextField
                  label={t('newProperty.city')}
                  autoComplete="address-level2"
                  value={form.city}
                  onChange={(e) => set('city')(e.target.value)}
                  error={err('city')}
                />
              </div>
              <div className={s.formRow}>
                <TextField
                  label={t('newProperty.housingCompany')}
                  value={form.housingCompany}
                  onChange={(e) => set('housingCompany')(e.target.value)}
                />
                <TextField
                  label={t('newProperty.businessId')}
                  placeholder="1234567-8"
                  value={form.housingCompanyBusinessId}
                  onChange={(e) => set('housingCompanyBusinessId')(e.target.value)}
                  error={err('housingCompanyBusinessId')}
                />
              </div>
              <div className={s.formRow}>
                <TextField
                  label={t('newProperty.areaM2')}
                  inputMode="decimal"
                  value={form.areaM2}
                  onChange={(e) => set('areaM2')(e.target.value)}
                  error={err('areaM2')}
                />
                <TextField
                  label={t('newProperty.roomLayout')}
                  hint={t('newProperty.roomLayoutHint')}
                  value={form.roomLayout}
                  onChange={(e) => set('roomLayout')(e.target.value)}
                />
              </div>
              <TextField
                label={t('newProperty.name')}
                hint={t('newProperty.nameHint')}
                value={form.name}
                onChange={(e) => set('name')(e.target.value)}
              />
              <div className={s.formActions}>
                <Link to="/vuokranantaja/asunnot" className="" style={{ alignSelf: 'center' }}>
                  {t('common.cancel')}
                </Link>
                <Button type="submit" variant="primary">
                  {t('common.next')}
                </Button>
              </div>
            </form>
          )}

          {step === 1 && (
            <form className={s.form} onSubmit={next} noValidate>
              <TextField
                label={t('newProperty.ownershipPercent')}
                hint={t('newProperty.ownershipHint')}
                inputMode="decimal"
                value={form.ownershipPercent}
                onChange={(e) => set('ownershipPercent')(e.target.value)}
                error={err('ownershipPercent')}
                style={{ maxWidth: 160 }}
              />
              <div className={s.formActions}>
                <Button variant="ghost" onClick={() => setStep(0)}>
                  {t('common.back')}
                </Button>
                <Button type="submit" variant="primary">
                  {t('common.next')}
                </Button>
              </div>
            </form>
          )}

          {step === 2 && (
            <form className={s.form} onSubmit={submit} noValidate>
              <ChoiceGroup<'later' | 'now'>
                name="tenancy"
                legend={t('newProperty.steps.tenancy')}
                value={form.tenancy}
                onChange={set('tenancy')}
                options={[
                  {
                    value: 'later',
                    title: t('newProperty.tenancyLater'),
                    text: t('newProperty.tenancyLaterText'),
                  },
                  {
                    value: 'now',
                    title: t('newProperty.tenancyNow'),
                    text: t('newProperty.tenancyNowText'),
                  },
                ]}
              />
              {create.isError && <Alert>{t(errorKey(create.error))}</Alert>}
              <div className={s.formActions}>
                <Button variant="ghost" onClick={() => setStep(1)}>
                  {t('common.back')}
                </Button>
                <Button type="submit" variant="primary" disabled={create.isPending}>
                  {t('newProperty.create')}
                </Button>
              </div>
            </form>
          )}
        </Panel>
      </div>
    </>
  );
}
